import { useLingui } from '@lingui/react/macro';
import { toast } from '@utima/ui';
import { Form, type FormProps, type TypedFormState } from '@utima/ui-informed';
import { useCallback, useState } from 'react';

import { AppSettings } from '@/constants/AppSettings';

import { ActionBar } from './ActionBar';
import { FormCol } from './FormCol';
import { FormGrid } from './FormGrid';
import { defaultAdapterContext, FormSchemaContext } from './FormSchemaContext';
import { UIFormDevtools } from './UIFormDevtools';
import { ConfirmDialog } from '../confirmDialog/ConfirmDialog';

export interface CRUDFormProps<T> extends FormProps<T> {
  columns?: boolean;
  onDelete?: () => Promise<unknown>;
  showDelete?: boolean;
  onSubmitError?: (error: unknown) => void;
  deleteDescription?: string;
  confirmationText?: string;
  confirmationLabel?: string;
}

/**
 * Wrapper around Form component, which provides CRUD actions and
 * confirmation dialogs for delete action. It also handles success
 * and failure notifications and provides 3-column layout.
 */
export function CRUDForm<T>({
  onDelete,
  onSubmit,
  children,
  readOnly,
  disabled = false,
  loading = false,
  columns = true,
  initialValues,
  showDelete,
  onSubmitError,
  deleteDescription,
  confirmationText,
  confirmationLabel,
  ...restProps
}: CRUDFormProps<T>) {
  const { t } = useLingui();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  /**
   * Default submit handler, handles error and success notifications.
   */
  const handleSubmit = useCallback(
    async (formState: TypedFormState<T>) => {
      try {
        setSubmitting(true);
        await onSubmit?.(formState);

        // Show success notification
        toast.success(t`Saved`, {
          description: t`Form was successfully saved.`,
        });
      } catch (error) {
        console.error(error);

        if (onSubmitError) {
          onSubmitError(error);
        } else if (AppSettings.debug || import.meta.env.DEV) {
          toast.error(t`Save failed`, {
            description: () => (
              <div>
                {t`Form contains errors.`}
                <pre className='mt-2 overflow-auto rounded-radius bg-slate-100 p-2 font-mono text-[11px]'>
                  {JSON.stringify(error, null, 2)}
                </pre>
              </div>
            ),
          });
        } else {
          toast.error(t`Save failed`, {
            description: t`Form contains errors.`,
          });
        }
      } finally {
        setSubmitting(false);
      }
    },
    [onSubmit, t, onSubmitError],
  );

  /**
   * Default failure handler, shows validation error notification.
   */
  const handleSubmitFailure = useCallback(() => {
    toast.error(t`Save failed`, {
      description: t`Form contains errors.`,
    });
  }, [t]);

  /**
   * Delete handler, opens confirmation modal and calls onDelete callback.
   */
  const handleDelete = useCallback(() => {
    setDialogOpen(true);
  }, []);

  return (
    <FormSchemaContext.Provider value={defaultAdapterContext}>
      <Form
        showOptional
        initialValues={initialValues}
        allowEmptyStrings
        onSubmit={handleSubmit}
        onSubmitFailure={handleSubmitFailure}
        readOnly={readOnly}
        loading={submitting}
        {...restProps}
      >
        {columns ? <FormGrid>{children}</FormGrid> : children}

        {!readOnly && (
          <ActionBar
            loading={submitting || loading}
            showDelete={!!initialValues && showDelete}
            onDelete={handleDelete}
          />
        )}

        <ConfirmDialog
          onConfirm={async () => onDelete?.()}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          description={deleteDescription}
          confirmationText={confirmationText}
          confirmationLabel={confirmationLabel}
        />

        <UIFormDevtools />
      </Form>
    </FormSchemaContext.Provider>
  );
}

// Shortcut for FormCol component
CRUDForm.Col = FormCol;
